/**
 * Utility functions for handling Pokemon images with automatic format detection
 * 
 * NEW Image Naming Conventions (pokemon-spirit folder):
 * | Form        | Normal              | Shiny                    |
 * |-------------|---------------------|--------------------------|
 * | Normal      | {id}.png            | {id}_shiny.png           |
 * | Mega        | {id}-Mega_X.png     | {id}-Mega_X_shiny.png    |
 * | Gigantamax  | {id}-Gigantamax.png | {id}-Gigantamax_shiny.png|
 * | Alolan      | {id}-Alola.png      | {id}-Alola_shiny.png     |
 * | Galarian    | {id}-Galarian.png   | {id}-Galarian_shiny.png  |
 * | Hisuian     | {id}-Hisuian.png    | {id}-Hisuian_shiny.png   |
 * | Paldean     | {id}-Paldean.png    | {id}-Paldean_shiny.png   |
 * | In-Game     | ingame-{id}.png     | ingame-{id}-s.png        |
 * | In-Game Regional | ingame-{id}-alola.png | ingame-{id}-alola-s.png |
 */

/**
 * Get the base numeric ID from a Pokemon ID string
 * รองรับรูปแบบ: "1", "3-Mega", "gourgeist_large", "ninetales_alolan"
 * @param {string} pokemonId - Pokemon ID
 * @returns {string} - Base numeric ID without leading zeros, or base name if not numeric
 */
export const getBaseId = (pokemonId) => {
  if (!pokemonId) return '1';
  const idStr = String(pokemonId);
  
  // If it's numeric (like "0001", "376"), extract base
  if (/^\d+/.test(idStr)) {
    return idStr.split(/[-_]/)[0].replace(/^0+/, '') || '1';
  }
  
  // If it's a name with underscore (like "gourgeist_large"), return base name
  if (idStr.includes('_')) {
    return idStr.split('_')[0];
  }
  
  // If it's a name with dash (like "gourgeist-large"), return base name
  if (idStr.includes('-')) {
    return idStr.split('-')[0];
  }
  
  // Return as-is if no separators
  return idStr;
};

/**
 * Get variant suffix from Pokemon ID
 * รองรับรูปแบบ: "3-Mega_X", "25-Gigantamax", "gourgeist_large", "ninetales_alolan"
 * @param {string} pokemonId - Pokemon ID (e.g., "1", "3-Mega_X", "gourgeist_large")
 * @returns {string|null} - Variant suffix or null for normal Pokemon
 */
export const getVariantSuffix = (pokemonId) => {
  if (!pokemonId) return null;
  const idStr = String(pokemonId);
  
  // Check for underscore pattern (e.g., "gourgeist_large", "ninetales_alolan")
  if (idStr.includes('_')) {
    const parts = idStr.split('_');
    if (parts.length > 1) {
      // Return variant part (e.g., "large" from "gourgeist_large")
      const variant = parts.slice(1).join('_');
      
      // Normalize variant names
      const variantLower = variant.toLowerCase();
      if (variantLower === 'large' || variantLower === 'small' || 
          variantLower === 'super' || variantLower === 'average') {
        return variant.charAt(0).toUpperCase() + variant.slice(1); // Capitalize first letter
      }
      if (variantLower === 'alolan' || variantLower === 'alola') {
        return 'Alola';
      }
      if (variantLower === 'galarian' || variantLower === 'galar') {
        return 'Galarian';
      }
      if (variantLower === 'hisuian' || variantLower === 'hisui') {
        return 'Hisuian';
      }
      if (variantLower === 'paldean' || variantLower === 'paldea') {
        return 'Paldean';
      }
      
      return variant;
    }
  }
  
  // Check for dash pattern (e.g., "3-Mega_X")
  if (idStr.includes('-')) {
    return idStr.split('-').slice(1).join('-');
  }
  
  return null;
};


/**
 * Build Pokemon spirit image filename
 * @param {string} pokemonId - Pokemon ID (e.g., "1", "150-Mega_X", "6-Gigantamax", "gourgeist_large")
 * @param {boolean} isShiny - Whether to get shiny version
 * @returns {string} - Image filename (e.g., "3.png", "150-Mega_X_shiny.png", "711-Large.png")
 */
export const getSpiritImageFilename = (pokemonId, isShiny = false) => {
  let baseId = getBaseId(pokemonId);
  const variant = getVariantSuffix(pokemonId);
  
  // ถ้า baseId เป็นชื่อ (ไม่ใช่ numeric) ให้แปลงเป็น numeric ID
  // เช่น "gourgeist" -> "711", "ninetales" -> "38"
  if (!/^\d+/.test(String(baseId))) {
    const numericId = getNumericIdFromName(baseId);
    if (numericId) {
      baseId = numericId;
    }
  }
  
  if (!variant) {
    // Normal Pokemon: {id}.png or {id}_shiny.png
    return `${baseId}${isShiny ? '_shiny' : ''}.png`;
  }
  
  // Handle Crowned forms - normalize variant name
  let normalizedVariant = variant;
  const variantLower = variant.toLowerCase();
  
  if (variantLower.includes('crowned_sword') || variantLower.includes('crownedsword')) {
    normalizedVariant = 'Crowned_Sword';
  } else if (variantLower.includes('crowned_shield') || variantLower.includes('crownedshield')) {
    normalizedVariant = 'Crowned_Shield';
  } else if (variantLower === 'alola' || variantLower === 'alolan' || variantLower.startsWith('alola')) {
    // Normalize Alolan variants (Alola, Alolan, etc.)
    normalizedVariant = 'Alola';
  } else if (variantLower === 'galarian' || variantLower === 'galar' || variantLower.startsWith('galarian')) {
    // Normalize Galarian variants
    normalizedVariant = 'Galarian';
  } else if (variantLower === 'hisuian' || variantLower === 'hisui' || variantLower.startsWith('hisuian')) {
    // Normalize Hisuian variants
    normalizedVariant = 'Hisuian';
  } else if (variantLower === 'paldean' || variantLower === 'paldea' || variantLower.startsWith('paldean')) {
    // Normalize Paldean variants (Paldean, Paldean_Aqua_Breed, etc.)
    // For spirit images, use base "Paldean" variant
    normalizedVariant = 'Paldean';
  } else if (variantLower === 'large' || variantLower === 'small' || 
             variantLower === 'super' || variantLower === 'average') {
    // Size variants - capitalize first letter
    normalizedVariant = variant.charAt(0).toUpperCase() + variant.slice(1);
  }
  
  // Variant Pokemon: {id}-{Variant}.png or {id}-{Variant}_shiny.png
  // Examples: 150-Mega_X.png, 150-Mega_X_shiny.png, 6-Gigantamax.png, 888-Crowned_Sword.png
  // Regional forms: 26-Alola.png, 52-Galarian.png, 58-Hisuian.png, 128-Paldean.png
  // Size variants: 711-Large.png, 711-Small.png, 711-Super.png, 711-Average.png
  return `${baseId}-${normalizedVariant}${isShiny ? '_shiny' : ''}.png`;
};

/**
 * Map Pokemon name to numeric ID for image filenames
 * @param {string} name - Pokemon name (e.g., "gourgeist", "ninetales")
 * @returns {string|null} - Numeric ID or null if not found
 */
const getNumericIdFromName = (name) => {
  const nameLower = String(name).toLowerCase();
  const nameToIdMap = {
    'gourgeist': '711',
    'ninetales': '38',
    'corsola': '222',
    'vulpix': '37',
    'pumpkaboo': '710',
    // Add more as needed
  };
  return nameToIdMap[nameLower] || null;
};

/**
 * Build in-game sprite filename
 * @param {string} pokemonId - Pokemon ID
 * @param {boolean} isShiny - Whether to get shiny version
 * @returns {string} - Image filename (e.g., "ingame-143.png", "ingame-143-s.png")
 */
export const getIngameImageFilename = (pokemonId, isShiny = false) => {
  let baseId = getBaseId(pokemonId);
  const variant = getVariantSuffix(pokemonId);
  
  // ถ้า baseId เป็นชื่อ (ไม่ใช่ numeric) ให้แปลงเป็น numeric ID
  // เช่น "gourgeist" -> "711", "ninetales" -> "38"
  if (!/^\d+/.test(String(baseId))) {
    const numericId = getNumericIdFromName(baseId);
    if (numericId) {
      baseId = numericId;
    }
  }
  
  // Handle Crowned forms, regional forms, and size variants
  if (variant) {
    const variantLower = variant.toLowerCase();
    
    // Crowned forms - use lowercase format: crownedsword, crownedshield
    if (variantLower.includes('crowned_sword') || variantLower.includes('crownedsword')) {
      return `ingame-${baseId}-crownedsword${isShiny ? '-s' : ''}.png`;
    } else if (variantLower.includes('crowned_shield') || variantLower.includes('crownedshield')) {
      return `ingame-${baseId}-crownedshield${isShiny ? '-s' : ''}.png`;
    }
    
    // Size variants (Gourgeist) - use lowercase format: large, small, super, average
    if (variantLower === 'large' || variantLower.includes('large')) {
      return `ingame-${baseId}-large${isShiny ? '-s' : ''}.png`;
    } else if (variantLower === 'small' || variantLower.includes('small')) {
      return `ingame-${baseId}-small${isShiny ? '-s' : ''}.png`;
    } else if (variantLower === 'super' || variantLower.includes('super')) {
      return `ingame-${baseId}-super${isShiny ? '-s' : ''}.png`;
    } else if (variantLower === 'average' || variantLower.includes('average')) {
      return `ingame-${baseId}-average${isShiny ? '-s' : ''}.png`;
    }
    
    // Regional forms - use lowercase format: alola, galarian, hisuian, paldean
    if (variantLower === 'alola' || variantLower === 'alolan' || variantLower.startsWith('alola')) {
      return `ingame-${baseId}-alola${isShiny ? '-s' : ''}.png`;
    } else if (variantLower === 'galarian' || variantLower.startsWith('galarian')) {
      return `ingame-${baseId}-galarian${isShiny ? '-s' : ''}.png`;
    } else if (variantLower === 'hisuian' || variantLower.startsWith('hisuian')) {
      return `ingame-${baseId}-hisuian${isShiny ? '-s' : ''}.png`;
    } else if (variantLower === 'paldean' || variantLower.startsWith('paldean')) {
      // For Paldean variants with sub-forms (e.g., Paldean_Aqua_Breed), use base "paldean"
      return `ingame-${baseId}-paldean${isShiny ? '-s' : ''}.png`;
    }
    
    // Generic variant - convert to lowercase with underscore/hyphen
    // e.g., "Mega_X" -> "mega-x", "Large" -> "large"
    const normalizedVariant = variantLower.replace(/_/g, '-');
    return `ingame-${baseId}-${normalizedVariant}${isShiny ? '-s' : ''}.png`;
  }
  
  return `ingame-${baseId}${isShiny ? '-s' : ''}.png`;
};

/**
 * Get legacy image filename for Mega/variant Pokemon
 * @param {string} pokemonId - Pokemon ID with variant (e.g., "6-Mega_X", "6-Mega_Y")
 * @returns {string|null} - Legacy filename or null if not a known variant
 */
export const getLegacyVariantFilename = (pokemonId) => {
  const baseId = getBaseId(pokemonId);
  const paddedId = baseId.padStart(4, '0');
  const variant = getVariantSuffix(pokemonId);
  
  if (!variant) return null;
  
  // Legacy naming conventions for Mega Pokemon
  // Mega (single form) = _0001, Mega_X = _0002, Mega_Y = _0003
  if (variant === 'Mega') return `${paddedId}_0001.png`;
  if (variant === 'Mega_X') return `${paddedId}_0002.png`;
  if (variant === 'Mega_Y') return `${paddedId}_0003.png`;
  
  // Regional forms
  if (variant === 'Alola') return `${paddedId}_Alolan.png`;
  if (variant === 'Galarian') return `${paddedId}_Galarian.png`;
  if (variant === 'Hisuian') return `${paddedId}_Hisuian.png`;
  if (variant === 'Paldean') return `${paddedId}_Paldean.png`;
  
  // Gigantamax - check if available
  if (variant === 'Gigantamax') return `${paddedId}_Gigantamax.png`;
  
  return null;
};

/**
 * Get all possible image sources for a Pokemon in priority order
 * @param {string} pokemonId - Pokemon ID (e.g., "1", "3-Mega", "25-Gigantamax")
 * @param {boolean} isShiny - Whether to get shiny version
 * @returns {string[]} - Array of image URLs in priority order
 */
export const getPokemonImageSrcList = (pokemonId, isShiny = false) => {
  // For Shadow Pokemon, use base Pokemon ID (remove Shadow prefix)
  let actualPokemonId = pokemonId;
  if (pokemonId && String(pokemonId).toLowerCase().includes('shadow')) {
    // Extract base Pokemon ID from Shadow Pokemon ID (e.g., "464-Shadow" -> "464")
    actualPokemonId = String(pokemonId).split('-')[0];
  }
  
  // For Crowned forms, keep the full ID to use specific images
  // Crowned forms have their own images: ingame-888-crownedsword.png, ingame-889-crownedshield.png
  // So we keep the full ID like "888-Crowned_Sword" or "889-Crowned_Shield"
  
  let baseId = getBaseId(actualPokemonId);
  const originalBaseId = baseId; // Keep original for reference
  
  // แปลงชื่อเป็น numeric ID ถ้า baseId เป็นชื่อ (ไม่ใช่ numeric)
  // เช่น "gourgeist" -> "711", "ninetales" -> "38"
  if (!/^\d+/.test(String(baseId))) {
    const numericId = getNumericIdFromName(baseId);
    if (numericId) {
      baseId = numericId; // Use numeric ID for all fallback paths
    }
  }
  
  const paddedId = baseId.padStart(4, '0');
  const variant = getVariantSuffix(actualPokemonId);
  
  const spiritFilename = getSpiritImageFilename(actualPokemonId, isShiny);
  const ingameFilename = getIngameImageFilename(actualPokemonId, isShiny);
  
  // Base Pokemon spirit filename (for fallback when variant image doesn't exist)
  // ใช้ numeric ID เท่านั้น (ไม่ใช้ชื่อเช่น gourgeist, ninetales)
  const baseSpiritFilename = /^\d+/.test(String(baseId)) 
    ? `${baseId}${isShiny ? '_shiny' : ''}.png`
    : null;
  
  // Legacy variant filename (for Mega, regional forms)
  const legacyVariantFilename = getLegacyVariantFilename(actualPokemonId);
  
  const sources = [];
  
  // PRIORITY: /images/pokemon-spirit/ เป็นหลัก (ตามที่ผู้ใช้กำหนด)
  // ใช้ PNG เป็น primary (WebP เป็น fallback)
  
  // 1. High quality spirit images (variant specific) - PRIMARY SOURCE (PNG first)
  if (spiritFilename) {
    // Try PNG first (primary)
    sources.push(`/images/pokemon-spirit/${spiritFilename}`);
    // Then WebP fallback
    const webpFilename = spiritFilename.replace(/\.(png|webp)$/i, '.webp');
    sources.push(`/images/pokemon-spirit/${webpFilename}`);
    
    // Also try with underscore format for variants (PNG first)
    // Extract numeric ID from spiritFilename if it exists (e.g., "711-Large.png" -> "711")
    if (variant && spiritFilename) {
      const numericMatch = spiritFilename.match(/^(\d+)-/);
      if (numericMatch) {
        const numericBaseId = numericMatch[1];
        const variantUnderscore = variant.replace(/-/g, '_').toLowerCase();
        sources.push(`/images/pokemon-spirit/${numericBaseId}_${variantUnderscore}.png`);
        sources.push(`/images/pokemon-spirit/${numericBaseId}_${variantUnderscore}.webp`);
      }
    }
  }
  
  // 2. In-game sprites in pokemon-spirit folder (fallback if spirit variant image not found)
  // รองรับ: /images/pokemon-spirit/ingame-711-large.png, /images/pokemon-spirit/ingame-38-alola.png
  if (variant) {
    sources.push(`/images/pokemon-spirit/${ingameFilename}`);
  }
  
  // 3. In-game sprites in pokemon_images_ingame folder (fallback if pokemon-spirit ingame not found)
  // รองรับ: /images/pokemon-spirit/711-Large.png ไม่มี -> /images/pokemon_images_ingame/ingame-711-large.png
  // รองรับ: /images/pokemon-spirit/38-Alola.png ไม่มี -> /images/pokemon_images_ingame/ingame-38-alola.png
  if (variant) {
    sources.push(`/images/pokemon_images_ingame/${ingameFilename}`);
  }
  
  // 4. Base Pokemon spirit image (for variants as fallback) - PNG first
  // ใช้เฉพาะถ้า baseId เป็น numeric (ไม่ใช้ชื่อเช่น gourgeist, ninetales)
  if (variant && baseSpiritFilename) {
    sources.push(`/images/pokemon-spirit/${baseSpiritFilename}`);
  }
  if (variant && /^\d+/.test(String(baseId))) {
    sources.push(`/images/pokemon-spirit/${baseId}.png`);
    sources.push(`/images/pokemon-spirit/${baseId}.webp`);
    // Also try padded version
    sources.push(`/images/pokemon-spirit/${paddedId}.png`);
    sources.push(`/images/pokemon-spirit/${paddedId}.webp`);
  }
  
  // 5. Base Pokemon (no variant) in pokemon-spirit - PNG first
  // ใช้เฉพาะถ้า baseId เป็น numeric (ไม่ใช้ชื่อเช่น gourgeist, ninetales)
  if (/^\d+/.test(String(baseId))) {
    sources.push(`/images/pokemon-spirit/${baseId}.png`);
    sources.push(`/images/pokemon-spirit/${baseId}.webp`);
    // Also try padded version
    sources.push(`/images/pokemon-spirit/${paddedId}.png`);
    sources.push(`/images/pokemon-spirit/${paddedId}.webp`);
  }
  
  // 6. Base in-game sprite (for non-variant Pokemon)
  if (!variant) {
    sources.push(`/images/pokemon_images_ingame/${ingameFilename}`);
  }
  
  // 7. Legacy pokemon folder (fallback only - ไม่ใช่ primary) - PNG first
  if (variant && legacyVariantFilename) {
    sources.push(`/images/pokemon/${legacyVariantFilename}`);
    const legacyWebp = legacyVariantFilename.replace(/\.(png|webp)$/i, '.webp');
    sources.push(`/images/pokemon/${legacyWebp}`);
  }
  sources.push(`/images/pokemon/${paddedId}.png`);
  sources.push(`/images/pokemon/${paddedId}.webp`);
  
  // 8. Placeholder - ใช้ 000.png เป็นสุดท้าย (ตามที่ผู้ใช้กำหนด)
  sources.push(`/images/pokemon-spirit/000.png`);
  sources.push(`/images/pokemon-spirit/1.png`); // Try base Pokemon as last resort
  sources.push(`/images/pokemon-spirit/1.webp`);
  sources.push(`/images/pokemon/000.png`);
  sources.push(`/images/pokemon/0000.png`);
  sources.push(`/images/pokemon/0000.webp`);
  
  return sources;
};

/**
 * Get primary Pokemon image URL (first source)
 * Backward compatible: accepts (pokemonId) or (pokemonId, isShiny) or (pokemonId, pokemonName)
 * @param {string} pokemonId - Pokemon ID
 * @param {boolean|string} isShinyOrName - Whether to get shiny version (boolean) or Pokemon name (string, ignored for backward compat)
 * @returns {string} - Primary image URL
 */
export const getPokemonImageUrl = (pokemonId, isShinyOrName = false) => {
  // Handle backward compatibility: if second param is a string (pokemon name), treat as normal image
  const isShiny = typeof isShinyOrName === 'boolean' ? isShinyOrName : false;
  return getPokemonImageSrcList(pokemonId, isShiny)[0];
};

/**
 * Get Pokemon image URL from Pokemon object
 * SINGLE SHARED UTILITY FUNCTION for consistent image handling across entire app
 * @param {object} pokemon - Pokemon object (can have id, Pokemon_ID, etc.)
 * @param {boolean} isShiny - Whether to get shiny version (default: false)
 * @returns {string} - Primary image URL
 */
export const getPokemonImage = (pokemon, isShiny = false) => {
  if (!pokemon) return '/images/pokemon-spirit/000.png';
  
  // Extract Pokemon ID from various possible fields
  const pokemonId = pokemon.id || 
                   pokemon.Pokemon_ID || 
                   pokemon.pokemon_id ||
                   pokemon.speciesId ||
                   pokemon.species_id ||
                   null;
  
  if (!pokemonId) return '/images/pokemon-spirit/000.png';
  
  // Use the shared image URL function
  return getPokemonImageUrl(pokemonId, isShiny);
};

/**
 * Create an image error handler that cycles through fallback sources
 * @param {string} pokemonId - Pokemon ID
 * @param {boolean} isShiny - Whether shiny version
 * @returns {function} - onError handler function
 */
export const createImageFallbackHandler = (pokemonId, isShiny = false) => {
  const sources = getPokemonImageSrcList(pokemonId, isShiny);
  let currentIndex = 0;
  
  return (e) => {
    currentIndex++;
    if (currentIndex < sources.length) {
      e.target.src = sources[currentIndex];
    }
  };
};

/**
 * Get image props with src and onError handler for a Pokemon
 * @param {string} pokemonId - Pokemon ID
 * @param {boolean} isShiny - Whether shiny version
 * @returns {object} - Object with src and onError properties
 */
export const getPokemonImageWithFallback = (pokemonId, isShiny = false) => {
  return {
    src: getPokemonImageUrl(pokemonId, isShiny),
    onError: createImageFallbackHandler(pokemonId, isShiny)
  };
};

/**
 * Legacy function - Convert Pokemon ID to image filename
 * Kept for backward compatibility
 */
export const getImageFilename = (pokemonId, pokemonName = '') => {
  if (!pokemonId) return '0000';
  
  const idStr = String(pokemonId);
  
  // Check for variant types
  if (idStr.includes('-')) {
    const parts = idStr.split('-');
    const baseId = parts[0];
    const variant = parts.slice(1).join('-');
    const paddedBaseId = baseId.padStart(4, '0');
    
    if (variant === 'Gigantamax') {
      const gmaxWithImages = ['25', '52', '68', '94', '99', '131', '133', '143', '569', '809', '812', '815', '818', '823', '826', '834', '839', '841', '844', '849', '851', '858', '861', '869', '879', '884', '892'];
      if (gmaxWithImages.includes(baseId)) {
        const cleanName = pokemonName.replace(/^Gigantamax\s+/i, '').replace(/\s+/g, '');
        if (cleanName) {
          return `${paddedBaseId}${cleanName}-Gigantamax`;
        }
      }
      return paddedBaseId;
    }
    
    if (variant === 'Mega') return `${paddedBaseId}_0001`;
    if (variant === 'Mega_X') return `${paddedBaseId}_0002`;
    if (variant === 'Mega_Y') return `${paddedBaseId}_0003`;
    if (variant === 'Alola') return `${paddedBaseId}_Alolan`;
    if (variant === 'Galarian' || variant.startsWith('Galarian')) return `${paddedBaseId}_Galarian`;
    if (variant === 'Hisuian' || variant.startsWith('Hisuian')) return `${paddedBaseId}_Hisuian`;
    if (variant === 'Paldean' || variant.startsWith('Paldean')) return `${paddedBaseId}_Paldean`;
    
    return `${paddedBaseId}_${variant}`;
  }
  
  return idStr.padStart(4, '0');
};

// Legacy functions removed - use getPokemonImageSrcList and createImageFallbackHandler instead

/**
 * Get Pokemon image with automatic format detection and error handling
 * @deprecated Use getPokemonImageWithFallback instead
 * @param {string} pokemonId - Pokemon ID
 * @param {string} basePath - Base path for images (ignored, kept for backward compatibility)
 * @returns {object} - Object with src and onError properties
 */
export const getPokemonImageProps = (pokemonId, basePath = "/images/pokemon/") => {
  return getPokemonImageWithFallback(pokemonId);
};

/**
 * Check if image exists by trying to load it
 * @param {string} url - Image URL
 * @returns {Promise<boolean>} - Promise that resolves to true if image exists
 */
export const checkImageExists = (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
};

/**
 * Get the best available image format for a Pokemon
 * @param {string} pokemonId - Pokemon ID
 * @param {string} basePath - Base path for images (default: "/images/pokemon/")
 * @returns {Promise<string>} - Promise that resolves to the best available image URL
 */
export const getBestPokemonImage = async (pokemonId, basePath = "/images/pokemon/") => {
  const formats = ['webp', 'png', 'jpg', 'jpeg', 'gif'];
  
  for (const format of formats) {
    const url = `${basePath}${pokemonId}.${format}`;
    const exists = await checkImageExists(url);
    if (exists) {
      return url;
    }
  }
  
  // Return placeholder if no image found
  return `${basePath}0000.webp`;
};

/**
 * Preload Pokemon images for better performance
 * @param {Array<string>} pokemonIds - Array of Pokemon IDs
 * @param {string} basePath - Base path for images (default: "/images/pokemon/")
 * @returns {Promise<Array>} - Promise that resolves when all images are preloaded
 */
export const preloadPokemonImages = async (pokemonIds, basePath = "/images/pokemon/") => {
  const preloadPromises = pokemonIds.map(async (id) => {
    const url = await getBestPokemonImage(id, basePath);
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ id, url, loaded: true });
      img.onerror = () => resolve({ id, url, loaded: false });
      img.src = url;
    });
  });
  
  return Promise.all(preloadPromises);
};

/**
 * Get Pokemon image URL from name (for counter Pokemon etc.)
 * Handles Mega, Shadow, Gigantamax, Alolan, Galarian prefixes
 * Uses NEW naming convention from pokemon-spirit folder
 * 
 * @param {string} pokemonName - Pokemon name (e.g., "Mega Rayquaza", "Shadow Mewtwo", "Gigantamax Charizard")
 * @returns {string} - Image URL (uses pokemon-spirit as primary source)
 */
export const getPokemonImageFromName = (pokemonName) => {
  if (!pokemonName) return `/images/pokemon/0000.png`;
  
  // Pokemon name to ID mapping (common Pokemon)
  // รองรับรูปแบบ: "Gourgeist (Large size)", "Alolan Ninetales", "Galarian Corsola"
  const pokemonNameToId = {
    // Starters and evolutions
    'bulbasaur': '1', 'ivysaur': '2', 'venusaur': '3',
    'charmander': '4', 'charmeleon': '5', 'charizard': '6',
    'squirtle': '7', 'wartortle': '8', 'blastoise': '9',
    // Pikachu line
    'pikachu': '25', 'raichu': '26',
    // Eevee evolutions
    'eevee': '133', 'vaporeon': '134', 'jolteon': '135', 'flareon': '136',
    'espeon': '196', 'umbreon': '197', 'leafeon': '470', 'glaceon': '471', 'sylveon': '700',
    // Legendaries Gen 1
    'mewtwo': '150', 'mew': '151',
    'articuno': '144', 'zapdos': '145', 'moltres': '146',
    // Popular Pokemon
    'gengar': '94', 'machamp': '68', 'alakazam': '65',
    'dragonite': '149', 'tyranitar': '248', 'salamence': '373',
    'metagross': '376', 'garchomp': '445', 'lucario': '448',
    'rayquaza': '384', 'groudon': '383', 'kyogre': '382',
    'dialga': '483', 'palkia': '484', 'giratina': '487',
    'reshiram': '643', 'zekrom': '644', 'kyurem': '646',
    'xerneas': '716', 'yveltal': '717', 'zygarde': '718',
    'solgaleo': '791', 'lunala': '792', 'necrozma': '800',
    'zacian': '888', 'zamazenta': '889', 'eternatus': '890',
    'calyrex': '898',
    // Ultra Beasts
    'nihilego': '793', 'buzzwole': '794', 'pheromosa': '795',
    'xurkitree': '796', 'celesteela': '797', 'kartana': '798', 'guzzlord': '799',
    'poipole': '803', 'naganadel': '804', 'stakataka': '805', 'blacephalon': '806',
    // Mythicals
    'darkrai': '491', 'arceus': '493', 'victini': '494',
    'genesect': '649', 'diancie': '719', 'hoopa': '720', 'volcanion': '721',
    'magearna': '801', 'marshadow': '802', 'zeraora': '807', 'meltan': '808', 'melmetal': '809',
    // Gen 8+
    'cinderace': '815', 'inteleon': '818', 'rillaboom': '812',
    'corviknight': '823', 'duraludon': '884', 'dragapult': '887',
    'centiskorch': '851',
    // Other notable
    'latios': '381', 'latias': '380',
    'darmanitan': '555', 'chandelure': '609',
    'blaziken': '257', 'sceptile': '254', 'swampert': '260',
    'infernape': '392', 'empoleon': '395',
    'togekiss': '468', 'roserade': '407', 'mamoswine': '473',
    'rhyperior': '464', 'electivire': '466', 'magmortar': '467',
    'weavile': '461', 'honchkrow': '430', 'mismagius': '429',
    'haxorus': '612', 'hydreigon': '635', 'volcarona': '637',
    'terrakion': '639', 'virizion': '640', 'cobalion': '638',
    'thundurus': '642', 'tornadus': '641', 'landorus': '645',
    'rampardos': '409', 'bastiodon': '411',
    'excadrill': '530', 'conkeldurr': '534',
    'chansey': '113', 'blissey': '242', 'snorlax': '143', 'lapras': '131',
    'butterfree': '12', 'beedrill': '15',
    // Variant Pokemon (for mapping)
    'ninetales': '38', 'corsola': '222', 'gourgeist': '711', 'pumpkaboo': '710',
  };

  // Clean up the name and extract variant info
  // รองรับรูปแบบ: "Gourgeist (Large size)", "Alolan Ninetales", "Galarian Corsola"
  let cleanName = pokemonName.toLowerCase().trim();
  let variantSuffix = '';

  // Check for size variants in parentheses (e.g., "Gourgeist (Large size)")
  const sizeMatch = cleanName.match(/\((.+?)\s*(?:size)?\)/);
  if (sizeMatch) {
    const sizeType = sizeMatch[1].toLowerCase();
    if (sizeType.includes('large')) {
      variantSuffix = '-Large';
      cleanName = cleanName.replace(/\(.*?\)/g, '').trim();
    } else if (sizeType.includes('small')) {
      variantSuffix = '-Small';
      cleanName = cleanName.replace(/\(.*?\)/g, '').trim();
    } else if (sizeType.includes('super')) {
      variantSuffix = '-Super';
      cleanName = cleanName.replace(/\(.*?\)/g, '').trim();
    } else if (sizeType.includes('average')) {
      variantSuffix = '-Average';
      cleanName = cleanName.replace(/\(.*?\)/g, '').trim();
    }
  }

  // Check for variants and build suffix according to NEW naming convention
  // NEW convention: {id}-{Variant}.png (e.g., 150-Mega_X.png, 26-Alola.png)
  if (!variantSuffix) {
    if (cleanName.startsWith('mega ')) {
      cleanName = cleanName.replace('mega ', '');
      // Check for X/Y variants
      if (cleanName.endsWith(' x')) {
        variantSuffix = '-Mega_X';
        cleanName = cleanName.replace(' x', '');
      } else if (cleanName.endsWith(' y')) {
        variantSuffix = '-Mega_Y';
        cleanName = cleanName.replace(' y', '');
      } else {
        variantSuffix = '-Mega';
      }
    } else if (cleanName.startsWith('shadow ')) {
      // Shadow Pokemon use base image (no variant suffix)
      cleanName = cleanName.replace('shadow ', '');
    } else if (cleanName.startsWith('gigantamax ')) {
      variantSuffix = '-Gigantamax';
      cleanName = cleanName.replace('gigantamax ', '');
    } else if (cleanName.startsWith('galarian ') || cleanName.includes(' galarian')) {
      variantSuffix = '-Galarian';
      cleanName = cleanName.replace(/galarian\s+/gi, '').trim();
    } else if (cleanName.startsWith('alolan ') || cleanName.includes(' alolan')) {
      variantSuffix = '-Alola';
      cleanName = cleanName.replace(/alolan\s+/gi, '').trim();
    } else if (cleanName.startsWith('hisuian ') || cleanName.includes(' hisuian')) {
      variantSuffix = '-Hisuian';
      cleanName = cleanName.replace(/hisuian\s+/gi, '').trim();
    } else if (cleanName.startsWith('paldean ') || cleanName.includes(' paldean')) {
      variantSuffix = '-Paldean';
      cleanName = cleanName.replace(/paldean\s+/gi, '').trim();
    } else if (cleanName.includes('crowned')) {
      // Crowned forms - use base ID
      cleanName = cleanName.replace('crowned shield ', '').replace('crowned sword ', '');
    }
  }

  // Get base ID
  const baseId = pokemonNameToId[cleanName] || '0';
  if (baseId === '0') {
    // Unknown Pokemon, return placeholder
    return `/images/pokemon/0000.png`;
  }

  // Return path using pokemon-spirit as PRIMARY source (PNG format)
  return `/images/pokemon-spirit/${baseId}${variantSuffix}.png`;
};

/**
 * Get all possible image sources for a Pokemon by name (with fallbacks)
 * @param {string} pokemonName - Pokemon name
 * @returns {string[]} - Array of image URLs in priority order
 */
export const getPokemonImageSourcesFromName = (pokemonName) => {
  const primaryUrl = getPokemonImageFromName(pokemonName);
  
  // Extract base ID from the primary URL path
  const match = primaryUrl.match(/pokemon-spirit\/(\d+)/);
  const baseId = match ? match[1] : '1';
  const paddedId = baseId.padStart(4, '0');
  
  return [
    primaryUrl, // Already uses pokemon-spirit (PNG)
    `/images/pokemon-spirit/${baseId}.png`, // Base Pokemon in pokemon-spirit (PNG first)
    `/images/pokemon-spirit/${baseId}.webp`, // Base Pokemon in pokemon-spirit (WebP fallback)
    `/images/pokemon_images_ingame/ingame-${baseId}.png`,
    `/images/pokemon/${paddedId}.png`, // Legacy fallback (PNG)
    `/images/pokemon/${paddedId}.webp`, // Legacy fallback (WebP)
    `/images/pokemon-spirit/1.png`, // Ultimate fallback (PNG)
    `/images/pokemon/0000.png`
  ];
};

/**
 * Get all possible Pokemon image sources for fallback
 * @param {string} pokemonId - Pokemon ID (e.g., "1", "3-Mega", "25-Gigantamax")
 * @param {string} pokemonName - Pokemon name for variant images
 * @param {string} basePath - Base path for images (default: "/images/pokemon/")
 * @returns {object} - Object containing all possible image sources
 */
export const getPokemonImageSources = (pokemonId, pokemonName = '', basePath = "/images/pokemon/") => {
  // Get the proper image filename based on ID and variant
  const imageFilename = getImageFilename(pokemonId, pokemonName);
  
  // Get base numeric ID for external API fallbacks
  const baseId = String(pokemonId).split('-')[0];
  const paddedBaseId = baseId.padStart(4, '0');
  
  // Also try the simple 4-digit padded version as fallback
  const simplePaddedId = String(pokemonId).replace(/-.*$/, '').padStart(4, '0');
  
  return {
    // Primary: computed image filename
    localWebp: `${basePath}${imageFilename}.webp`,
    localPng: `${basePath}${imageFilename}.png`,
    localJpg: `${basePath}${imageFilename}.jpg`,
    localJpeg: `${basePath}${imageFilename}.jpeg`,
    localGif: `${basePath}${imageFilename}.gif`,
    local: `${basePath}${imageFilename}.png`,
    
    // Simple local format (for compatibility) - uses computed filename
    localSimple: `${basePath}${imageFilename}.webp`,
    
    // Fallback to base Pokemon image (for variants that might not have images)
    localBase: `${basePath}${simplePaddedId}.webp`,
    
    // Official artwork from Pokemon API (only works for base Pokemon)
    officialArtwork: `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${baseId}.png`,
    
    // Pokemon Home artwork
    pokemonHome: `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/home/${baseId}.png`,
    
    // Regular sprite
    sprite: `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${baseId}.png`,
    
    // Dream World artwork
    dreamWorld: `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/dream-world/${baseId}.svg`,
    
    // Placeholder
    placeholder: `${basePath}0000.webp`
  };
};